/**
 * MobileBarNavigation — Barra de navegação mobile flutuante moderna.
 *
 * Design 2026: pill largo e elevado com vidro fosco, ícone ativo com pill
 * de destaque colorido, label em fonte pequena e clean.
 * Inspirado nos padrões iOS 18 / Material You mais recentes.
 */

import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { Share, House, History, FamilyRestroom } from "@mui/icons-material";
import { currentFeatures } from "@/configs/Settings";
import FluxVetLogo from "../../../../../assets/fluxvet-logo.png";
import FluxmedLogo from "../../../../../assets/fluxmed-logo.png";

const MobileBarNavigation: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const navMenuItems = [
    { path: "/home", label: "Início", icon: House },
    { path: "/myfamily", label: "Família", icon: FamilyRestroom },
    { path: "/consent", label: "Acesso", icon: Share },
  ];

  const vetMenuItems = [
    { path: "/home", label: "Início", icon: House },
    { path: "/history", label: "Histórico", icon: History },
  ];

  const getMenuItems = () => {
    const baseItems = currentFeatures?.defaultApp ? navMenuItems : vetMenuItems;
    if (window.ReactNativeWebView) {
      const webViewItem = currentFeatures?.defaultApp
        ? { path: "/fluxvet", label: "Fluxvet", icon: () => <img src={FluxVetLogo} alt="Fluxvet" style={{ width: 20, height: 20 }} /> }
        : { path: "/fluxmed", label: "Fluxmed", icon: () => <img src={FluxmedLogo} alt="Fluxmed" style={{ width: 20, height: 20 }} /> };
      return [...baseItems, webViewItem];
    }
    return baseItems;
  };

  const menuItems = getMenuItems();

  const handlePress = (path: string) => {
    if (path === "/fluxvet" || path === "/fluxmed") {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          currentFeatures?.defaultApp ? "Change Fluxvet" : "Change Fluxmed",
        );
      }
    } else {
      navigate(path);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 4,
        left: "50%",
        transform: "translateX(-50%)",
        width: "calc(100% - 32px)",
        maxWidth: 420,
        display: { xs: "flex", md: "none" },
        zIndex: theme.zIndex.drawer + 2,
        pointerEvents: "none",
      }}
    >
      {/* Pill principal */}
      <Box
        sx={{
          pointerEvents: "auto",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          px: 2,
          py: 0.75,
          borderRadius: "24px",
          /* Vidro fosco escuro — visual premium */
          bgcolor: alpha(theme.palette.mode === "dark" ? "#1a1a2e" : "#1C1C2E", 0.92),
          backdropFilter: "blur(24px) saturate(200%)",
          WebkitBackdropFilter: "blur(24px) saturate(200%)",
          border: `1px solid ${alpha("#ffffff", 0.08)}`,
          boxShadow: [
            `0 20px 40px ${alpha("#000", 0.3)}`,
            `0 8px 16px ${alpha("#000", 0.2)}`,
            `inset 0 1px 0 ${alpha("#fff", 0.06)}`,
          ].join(", "),
        }}
      >
        {menuItems.map((item) => {
            const isActive =
              pathname === item.path ||
              (item.path !== "/home" && pathname.startsWith(item.path));
            const Icon = item.icon as React.ElementType;

            return (
              <Box
                key={item.path}
                component="button"
                onClick={() => handlePress(item.path)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  flex: 1,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  py: 0.25,
                  borderRadius: 2,
                  transition: "all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  "&:active": { transform: "scale(0.88)" },
                }}
              >
                {/* Wrapper do ícone com pill de destaque */}
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 46,
                    height: 25,
                    borderRadius: "14px",
                    transition: "all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    /* Ativo: pill colorido atrás do ícone */
                    /* Fundo branco suave para o item ativo — legível sobre o dark */
                    bgcolor: isActive ? alpha("#ffffff", 0.15) : "transparent",
                    boxShadow: isActive ? `0 0 14px ${alpha("#ffffff", 0.1)}` : "none",
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 20,
                      transition: "color 0.18s ease, transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)",
                      color: isActive ? "#ffffff" : alpha("#ffffff", 0.55),
                      transform: isActive ? "translateY(-1px)" : "none",
                    }}
                  />
                </Box>

                {/* Label com fonte clean e pequena */}
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: isActive ? "0.02em" : "0.01em",
                    lineHeight: 1,
                    color: isActive ? "#ffffff" : alpha("#ffffff", 0.5),
                    transition: "color 0.18s ease, font-weight 0.18s ease",
                    fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                    userSelect: "none",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export { MobileBarNavigation };
